import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Award,
  ClipboardCheck,
  FileCheck,
  CheckCircle,
  ArrowRight,
  ShieldCheck,
  Scale,
  Building2,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const frameworks = [
  {
    icon: Award,
    name: 'ISO 27001',
    description: 'Information security management system implementation and certification support.',
    timeline: '6-12 months typical',
  },
  {
    icon: ShieldCheck,
    name: 'SOC 2',
    description: 'Type I and Type II readiness, implementation, and audit support.',
    timeline: '3-9 months typical',
  },
  {
    icon: Scale,
    name: 'GDPR & Privacy',
    description: 'Data protection compliance, DPIAs, and privacy programme development.',
    timeline: 'Ongoing programme',
  },
  {
    icon: Building2,
    name: 'Industry-Specific',
    description: 'HIPAA, PCI-DSS, NIS2, DORA, and sector-specific regulatory requirements.',
    timeline: 'Varies by framework',
  },
];

const services = [
  {
    icon: ClipboardCheck,
    title: 'Gap Analysis',
    description:
      'Comprehensive assessment of current state against framework requirements, identifying gaps and prioritising remediation.',
  },
  {
    icon: FileCheck,
    title: 'Control Implementation',
    description:
      'Practical implementation of required controls, policies, and procedures that satisfy auditors while improving security.',
  },
  {
    icon: Users,
    title: 'Audit Support',
    description:
      'Hands-on support through the audit process, including evidence preparation and auditor relationship management.',
  },
  {
    icon: CheckCircle,
    title: 'Compliance Automation',
    description:
      'Implementation of tools and processes to streamline ongoing compliance and reduce manual effort.',
  },
];

const benefits = [
  'Win enterprise customers who require compliance certifications',
  'Reduce audit preparation time and stress',
  'Pass audits on the first attempt',
  'Integrate compliance with actual security improvement',
  'Maintain certifications with minimal ongoing burden',
  'Stay ahead of evolving regulatory requirements',
];

export default function CompliancePage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Compliance & Governance',
    description:
      'Navigate complex regulatory landscapes with practical, efficient compliance programmes',
    url: 'https://thecisonetwork.com/services/compliance',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Compliance & Governance"
        subtitle="Navigate complex regulatory landscapes with practical, efficient compliance programmes that satisfy auditors while improving your security"
        eyebrow="Our Services"
        backgroundImage={getServiceCard('compliance')?.image}
        backgroundAlt={getServiceCard('compliance')?.imageAlt}
        seoTitle="Cybersecurity Compliance Services | The CISO Network"
        seoDescription="Expert compliance implementation for SOC 2, ISO 27001, GDPR, and industry-specific frameworks. Practical controls that satisfy auditors and improve security."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Compliance Does Not Have to Be a Burden
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Compliance requirements are multiplying. From SOC 2 and ISO 27001 to GDPR,
                  industry-specific regulations, and customer security questionnaires, organisations
                  face an ever-growing list of obligations. Many struggle to navigate this landscape
                  efficiently.
                </p>
                <p>
                  We help you implement practical, efficient compliance programmes that satisfy
                  auditors while actually improving your{' '}
                  <Link to="/services/risk-assessment" className="text-secondary hover:underline">
                    security posture
                  </Link>
                  . Our approach focuses on sustainable compliance - building systems and processes
                  that maintain certification with minimal ongoing effort.
                </p>
                <p>
                  Whether you are pursuing your first certification or maintaining multiple
                  frameworks, our fractional CISOs bring the expertise to guide you through
                  successfully.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6 text-center">
                When You Need Compliance Support
              </h3>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'Pursuing SOC 2, ISO 27001, or other certification',
                    'Enterprise customers require compliance evidence',
                    'Facing regulatory requirements (GDPR, HIPAA, etc.)',
                    'Preparing for investment or acquisition',
                    <>
                      Previous audit identified{' '}
                      <Link
                        to="/services/risk-assessment"
                        className="text-secondary hover:underline"
                      >
                        significant gaps
                      </Link>
                    </>,
                    'Need to maintain multiple frameworks efficiently',
                    'Expanding into regulated markets or industries',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frameworks */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Frameworks We Support
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Expertise across all major security and privacy frameworks
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {frameworks.map((framework, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center">
                    <framework.icon className="h-6 w-6 text-teal" />
                  </div>
                  <span className="text-sm text-muted-foreground bg-background px-3 py-1 rounded-full">
                    {framework.timeline}
                  </span>
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                  {framework.name}
                </h3>
                <p className="text-muted-foreground">{framework.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Our Compliance Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              End-to-end support from initial assessment through certification and beyond
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <div key={i} className="bg-background rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <service.icon className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="space-y-6">
                {[
                  {
                    title: 'Practical Controls',
                    description:
                      'We design controls that work in practice, not just on paper. Security and compliance should reinforce each other.',
                  },
                  {
                    title: 'Audit-Ready Documentation',
                    description:
                      'Comprehensive policy libraries and evidence packages that satisfy the toughest auditors.',
                  },
                  {
                    title: 'Evidence Automation',
                    description:
                      'We implement processes and tools that automatically collect and organise compliance evidence.',
                  },
                  {
                    title: 'Ongoing Maintenance',
                    description:
                      'Sustainable programmes that maintain compliance with minimal ongoing effort and cost.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-bold text-sm">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-1">
                        {item.title}
                      </h4>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Our Approach to Compliance
              </h2>
              <p className="text-muted-foreground mb-6">
                We believe compliance should be a byproduct of good security, not a separate burden.
                Our approach integrates compliance requirements into your overall security
                programme, ensuring that every control serves both purposes.
              </p>
              <div className="p-6 bg-white rounded-xl border border-border">
                <h4 className="font-heading font-semibold text-foreground mb-3">
                  Typical Engagement
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gap Analysis</span>
                    <span className="text-foreground">2-4 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Implementation</span>
                    <span className="text-foreground">3-9 months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Audit Support</span>
                    <span className="text-foreground">Ongoing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8">
              The Business Value of Compliance
            </h2>
            <div className="grid sm:grid-cols-2 gap-6 text-left">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-background rounded-xl">
                  <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Achieve Compliance?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your compliance goals and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can guide you to certification.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
